<template>
  <div>
    <!-- IndexCard com loading desmonta a tabela e o :options.sync re-dispara em loop. -->
    <index-card :loading="false">
      <template slot="header">
        <div class="d-flex align-center flex-wrap" style="width: 100%; gap: 8px;">
          <div class="d-flex align-center flex-shrink-0">
            <v-btn text icon large to="/" exact>
              <v-icon class="white--text">mdi-arrow-left</v-icon>
            </v-btn>
          </div>
          <div class="index-clients-title-block text-center flex-grow-1 px-2" style="min-width: 160px;">
            <div class="headline white--text font-weight-medium mb-0">Mensagens WhatsApp</div>
            <p class="mb-0 mt-1 white--text" style="font-size: 0.8125rem; opacity: 0.88;">
              Fila de envio e status das mensagens
            </p>
          </div>
          <div class="d-flex align-center flex-shrink-0">
            <v-btn text icon color="white" aria-label="Configurações" @click="openSettingsDialog">
              <v-icon class="white--text">mdi-cog</v-icon>
            </v-btn>
            <v-btn text icon color="white" aria-label="Atualizar" @click="loadMessages(true)">
              <v-icon class="white--text">mdi-refresh</v-icon>
            </v-btn>
          </div>
        </div>
      </template>

      <template slot="subheader">
        <div class="whatsapp-messages-filters">
          <filter-data
            :with_client="false"
            :with_date_start="false"
            :with_date_end="false"
            :extra-filters-count="1"
            :loading_block="loading"
            @doneFilter="handleDoneFilter"
          >
            <template v-slot:extra-filters>
              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedStatus"
                  :items="statusOptions"
                  label="Status"
                  item-text="label"
                  item-value="value"
                  clearable
                  hide-details="auto"
                  outlined
                  dense
                  prepend-inner-icon="mdi-list-status"
                />
              </v-col>
            </template>
          </filter-data>
        </div>
      </template>

      <template slot="data_table">
        <v-data-table
          :headers="headers"
          :items="messages"
          :options.sync="pagination"
          :loading="loading"
          :server-items-length="total"
          class="elevation-4"
          mobile-breakpoint="1"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
          }"
        >
          <template v-slot:item.status="{ item }">
            <v-chip small :color="statusChipColor(item.status)" text-color="white">
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.number="{ item }">
            <span class="whatsapp-number-cell">{{ formatPhoneBr(item.number) }}</span>
          </template>

          <template v-slot:item.message="{ item }">
            <v-tooltip bottom max-width="420" content-class="whatsapp-messages-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="text-truncate d-inline-block" style="max-width: 220px;">
                  {{ truncateMessage(item.message) }}
                </span>
              </template>
              <span style="white-space: pre-wrap;">{{ item.message }}</span>
            </v-tooltip>
          </template>

          <template v-slot:item.error_message="{ item }">
            <v-tooltip
              v-if="item.error_message"
              bottom
              max-width="400"
              content-class="whatsapp-messages-tooltip"
            >
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="text-truncate d-inline-block" style="max-width: 200px;">
                  {{ item.error_message }}
                </span>
              </template>
              <span>{{ item.error_message }}</span>
            </v-tooltip>
            <span v-else class="text--secondary">—</span>
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>

          <template v-slot:item.sent_at="{ item }">
            {{ item.sent_at ? formatDateTime(item.sent_at) : '—' }}
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center align-center" style="gap: 2px;">
              <template v-if="item.status === 'error'">
                <v-tooltip bottom content-class="whatsapp-messages-tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="secondary"
                      v-bind="attrs"
                      v-on="on"
                      :loading="requeueLoadingId === item.id"
                      aria-label="Reenfileirar"
                      @click.stop="requeueMessage(item)"
                    >
                      <v-icon>mdi-replay</v-icon>
                    </v-btn>
                  </template>
                  <span>Reenfileirar</span>
                </v-tooltip>
                <v-tooltip bottom content-class="whatsapp-messages-tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      aria-label="Mudar número"
                      @click.stop="openChangeNumberDialog(item)"
                    >
                      <!-- mdi-phone-edit não existe no MDI empacotado com Vuetify 2.2 -->
                      <v-icon>mdi-phone-plus</v-icon>
                    </v-btn>
                  </template>
                  <span>Mudar número</span>
                </v-tooltip>
              </template>
            </div>
          </template>
        </v-data-table>
      </template>
    </index-card>

    <v-dialog v-model="changeNumberDialog" max-width="480" @click:outside="closeChangeNumberDialog">
      <v-card>
        <v-card-title class="headline">Corrigir número do WhatsApp</v-card-title>
        <v-card-text v-if="changeNumberItem" class="py-2 text--secondary body-2">
          <span>Vamos alterar apenas o número do WhatsApp dessa mensagem. <b>Altere no perfil do cliente para mensagens futuras!</b>.</span>
        </v-card-text>
        <v-divider />
        <v-card-text class="pt-4">
          <v-form ref="changeNumberForm" lazy-validation>
            <v-text-field
              v-model="changeNumberValue"
              v-mask="'(##) #####-####'"
              label="Número WhatsApp"
              prepend-icon="mdi-whatsapp"
              placeholder="(00) 00000-0000"
              persistent-hint
              outlined
              dense
              hide-details="auto"
              :rules="changeNumberRules"
              autocomplete="off"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeChangeNumberDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="changeNumberSaving" @click="submitChangeNumber">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="settingsDialog" max-width="900">
      <v-card>
        <v-card-title class="headline d-flex justify-space-between align-center">
          <span>Configurações WhatsApp</span>
          <v-btn icon @click="settingsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-card flat class="pa-0">
            <v-card-title class="px-0">
              <v-icon class="mr-2" color="green">mdi-whatsapp</v-icon>
              Status da conexão (WhatsApp)
            </v-card-title>
            <v-card-text class="px-0">
              <div class="mb-3 d-flex align-center">
                <v-chip small :color="instanceChipColor" text-color="white">
                  {{ instanceChipLabel }}
                </v-chip>
                <v-btn
                  icon small class="primary white--text ml-2"
                  :loading="instanceLoading"
                  :disabled="instanceLoading"
                  @click="getWhatsappStatus"
                >
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </div>
              <div class="mb-2">
                <strong>Instância:</strong> {{ whatsappInstanceStatus?.instance_name || '-' }}
              </div>

              <div v-if="instanceConnecting" class="mt-4">
                <p class="text-body-2 mb-2">
                  Escaneie o QR Code com o WhatsApp para conectar esta instância.
                </p>
                <div class="d-flex align-center" style="min-height: 200px;">
                  <v-progress-circular
                    v-if="instanceQrcodeLoading && !instanceQrcode"
                    indeterminate
                    color="primary"
                  />
                  <img
                    v-else-if="instanceQrcode"
                    :src="instanceQrcode"
                    alt="QR Code WhatsApp"
                    class="elevation-2"
                    style="max-width: 280px; height: auto;"
                  />
                  <span v-else-if="!instanceQrcodeLoading" class="text--secondary text-caption">
                    Não foi possível carregar o QR Code. Toque em atualizar.
                  </span>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-divider class="my-5" />

          <v-card flat class="pa-0">
            <v-card-title class="px-0">
              <v-icon class="mr-2" color="primary">mdi-send</v-icon>
              Configurações de envio de mensagens e emails
            </v-card-title>
            <v-card-text class="px-0">
              <v-select
                v-model="selectedStrategies"
                :items="strategyOptions"
                label="Quando enviar lembretes (WhatsApp e Boleto)"
                item-text="label"
                item-value="value"
                prepend-icon="mdi-calendar-clock"
                multiple
                chips
                :disabled="settingsLoading || settingsSaving || !canEditWppSettings"
              />
              <div class="d-flex align-center justify-end mt-4">
                <f-btn
                  save
                  small
                  :loading="settingsSaving"
                  :disabled="settingsLoading || settingsSaving || !selectedStrategies.length || !canEditWppSettings"
                  @click="saveSettings"
                ></f-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from '@/api/index'
import IndexCard from '@/_backoffice/_registrations/components/shared/index/IndexCard.vue'
import { mask } from 'vue-the-mask'

export default {
  name: 'WhatsappMessages',
  components: {
    IndexCard,
    FilterData: () => import('@/components/shared/FilterData.vue'),
  },
  directives: { mask },
  data() {
    return {
      loading: false,
      messages: [],
      total: 0,
      statusFilter: null,
      selectedStatus: null,
      statusOptions: [
        { label: 'Pendente', value: 'pending' },
        { label: 'Enviado', value: 'sended' },
        { label: 'Erro', value: 'error' },
      ],
      pagination: {
        page: 1,
        itemsPerPage: 25,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
      },
      headers: [
        { text: 'ID', value: 'id', align: 'center', width: '72' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: 'Número', value: 'number', align: 'center', width: '188' },
        { text: 'Mensagem', value: 'message', align: 'left' },
        { text: 'Erro', value: 'error_message', align: 'left' },
        { text: 'Criado em', value: 'created_at', align: 'center' },
        { text: 'Enviado em', value: 'sent_at', align: 'center' },
        { text: 'Ações', value: 'actions', align: 'center', sortable: false, width: '88' },
      ],
      requeueLoadingId: null,
      changeNumberDialog: false,
      changeNumberItem: null,
      changeNumberValue: '',
      changeNumberSaving: false,
      settingsDialog: false,
      settingsLoading: false,
      settingsSaving: false,
      selectedStrategies: [],
      whatsappInstanceStatus: null,
      instanceLoading: false,
      instanceQrcode: null,
      instanceQrcodeLoading: false,
      strategyOptions: [
        { label: 'Enviar 3 dias após vencido', value: 'send_3_days_overdue' },
        { label: 'Enviar 5 dias após vencido', value: 'send_5_days_overdue' },
        { label: 'Enviar 10 dias após vencido', value: 'send_10_days_overdue' },
        { label: 'Enviar no dia do vencimento', value: 'send_on_due_date' },
        { label: 'Enviar 3 dias antes do vencimento', value: 'send_3_days_before' },
        { label: 'Enviar quando pedido estiver em entrega', value: 'send_when_order_is_delivering' },
        { label: 'Enviar no ato gerador do boleto', value: 'send_on_bill_generation' },
      ],
      changeNumberRules: [
        (v) => !!(v && String(v).trim()) || 'Informe o número',
        (v) => {
          const n = this.normalizeWhatsappDigitsForApi(v)
          if (n.length >= 12 && n.length <= 13) return true
          return 'Use (DDD) XXXXX-XXXX (celular) ou (DDD) XXXX-XXXX (10 dígitos após DDD)'
        },
      ],
    }
  },
  computed: {
    canEditWppSettings() {
      return this.isDirector()
    },
    rawInstanceState() {
      const status = this.whatsappInstanceStatus?.status
      if (!status || status.error) return null
      return status.instance?.state || status.state || status.connectionState || null
    },
    instanceConnecting() {
      const s = this.rawInstanceState
      if (s == null) return false
      const normalized = String(s).toLowerCase()
      return normalized === 'connecting' || normalized === 'close'
    },
    instanceConnected() {
      const status = this.whatsappInstanceStatus?.status
      if (!status) return false
      const err = status?.error || this.whatsappInstanceStatus?.error
      if (err) return false
      const s = this.rawInstanceState
      if (!s) return false
      const normalized = String(s).toLowerCase()
      return normalized === 'open' || normalized === 'connected'
    },
    instanceChipColor() {
      if (this.instanceConnecting) return 'warning'
      if (this.instanceConnected) return 'success'
      return 'error'
    },
    instanceChipLabel() {
      if (this.instanceConnecting) return 'Conectando — escaneie o QR Code'
      if (this.instanceConnected) return 'Conectado'
      return 'Não conectado'
    },
  },
  methods: {
    /** JSON:API (`id`, `type`, `attributes`) → objeto plano usado na tabela. */
    normalizeWhatsappMessageRow(resource) {
      if (!resource) return {}
      if (resource.attributes === undefined) {
        return resource
      }
      const attrs = resource.attributes || {}
      return { ...attrs, id: Number(resource.id) }
    },
    handleDoneFilter() {
      this.statusFilter = this.selectedStatus
      this.loadMessages(true)
    },
    loadMessages(resetPage = false) {
      this.loading = true
      if (resetPage) {
        this.pagination.page = 1
      }
      const { page, itemsPerPage } = this.pagination
      const params = {
        page,
        limit: itemsPerPage,
      }
      if (this.statusFilter) {
        params.status = this.statusFilter
      }
      Api.WhatsappMessage.index(params)
        .then((res) => res.data)
        .then((body) => {
          const rows = body.data || []
          this.messages = rows.map((r) => this.normalizeWhatsappMessageRow(r))
          this.total = body.meta?.total ?? body.total ?? 0
        })
        .catch((err) => {
          console.error(err)
          this.addNotification({ type: 'error', msg: 'Não foi possível carregar as mensagens do WhatsApp.' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    statusLabel(status) {
      const map = {
        pending: 'Pendente',
        sended: 'Enviado',
        error: 'Erro',
      }
      return map[status] || status
    },
    statusChipColor(status) {
      const map = {
        pending: 'orange darken-2',
        sended: 'green darken-2',
        error: 'red darken-2',
      }
      return map[status] || 'grey'
    },
    truncateMessage(text) {
      if (!text) return ''
      return text.length > 120 ? `${text.slice(0, 120)}…` : text
    },
    formatDateTime(value) {
      if (!value) return '—'
      return this.$moment(value).format('L LT')
    },
    digitsOnly(raw) {
      if (!raw) return ''
      return String(raw).replace(/\D/g, '')
    },
    /** Ex.: +55 (31) 98765-4321 (móvel) ou +55 (31) 3333-4444 (fixo). */
    formatPhoneBr(raw) {
      const d = this.digitsOnly(raw)
      if (!d) return '—'
      let normalized = d
      if (normalized.length === 11 && !normalized.startsWith('55')) {
        normalized = `55${normalized}`
      }
      if (!normalized.startsWith('55')) {
        return d
      }
      const rest = normalized.slice(2)
      if (rest.length < 10) {
        return `+${normalized}`
      }
      const ddd = rest.slice(0, 2)
      const local = rest.slice(2)
      if (local.length === 9) {
        return `+55 (${ddd}) ${local.slice(0, 5)}-${local.slice(5)}`
      }
      if (local.length === 8) {
        return `+55 (${ddd}) ${local.slice(0, 4)}-${local.slice(4)}`
      }
      return `+55 (${ddd}) ${local}`
    },
    /** Mesma lógica de `ClientPaymentReminderPreference` / show do cliente. */
    formatPhoneNumberLikeClient(number) {
      if (!number) return null
      const cleanNumber = String(number).replace(/\D/g, '')
      if (cleanNumber.length >= 11) {
        const ddd = cleanNumber.slice(-11, -9)
        const firstPart = cleanNumber.slice(-9, -4)
        const secondPart = cleanNumber.slice(-4)
        return `(${ddd}) ${firstPart}-${secondPart}`
      }
      if (cleanNumber.length >= 9) {
        const ddd = cleanNumber.slice(-9, -7)
        const firstPart = cleanNumber.slice(-7, -4)
        const secondPart = cleanNumber.slice(-4)
        return `(${ddd}) ${firstPart}-${secondPart}`
      }
      return cleanNumber || null
    },
    /** Campo mascarado (DDD + número) → dígitos com DDI 55 para a API. */
    normalizeWhatsappDigitsForApi(maskedOrDigits) {
      let d = String(maskedOrDigits || '').replace(/\D/g, '')
      if (!d) return ''
      if (d.startsWith('55') && d.length >= 12) return d
      if (d.length === 11 || d.length === 10) return `55${d}`
      return d
    },
    requeueMessage(item) {
      this.requeueLoadingId = item.id
      Api.WhatsappMessage.requeue(item.id)
        .then(() => {
          this.addNotification({ type: 'success', msg: 'Mensagem reenfileirada para envio.' })
          this.loadMessages()
        })
        .catch((err) => {
          console.error(err)
          const msg = err?.response?.data?.errors?.join?.(', ') || 'Não foi possível reenfileirar a mensagem.'
          this.addNotification({ type: 'error', msg })
        })
        .finally(() => {
          this.requeueLoadingId = null
        })
    },
    openChangeNumberDialog(item) {
      this.changeNumberItem = item
      const clean = this.digitsOnly(item.number || '')
      this.changeNumberValue = this.formatPhoneNumberLikeClient(clean) || ''
      this.changeNumberDialog = true
      this.$nextTick(() => this.$refs.changeNumberForm?.resetValidation?.())
    },
    closeChangeNumberDialog() {
      this.changeNumberDialog = false
      this.changeNumberItem = null
      this.changeNumberValue = ''
      this.changeNumberSaving = false
    },
    submitChangeNumber() {
      if (!this.changeNumberItem || !this.$refs.changeNumberForm?.validate?.()) return
      const digits = this.normalizeWhatsappDigitsForApi(this.changeNumberValue)
      this.changeNumberSaving = true
      Api.WhatsappMessage.update(this.changeNumberItem.id, {
        whatsapp_message: { number: digits },
      })
        .then(() => {
          this.addNotification({ type: 'success', msg: 'Número atualizado.' })
          this.closeChangeNumberDialog()
          this.loadMessages()
        })
        .catch((err) => {
          console.error(err)
          const msg =
            err?.response?.data?.errors?.join?.(', ') || 'Não foi possível atualizar o número.'
          this.addNotification({ type: 'error', msg })
        })
        .finally(() => {
          this.changeNumberSaving = false
        })
    },
    openSettingsDialog() {
      this.settingsDialog = true
      this.getSettingsData()
      this.getWhatsappStatus()
    },
    normalizeStrategies(values) {
      return Array(values)
        .flatMap((value) => String(value || '').trim().split(','))
        .map((value) => value.trim())
        .filter((value) => value && value !== '{}' && value !== '[]' && value !== 'null')
    },
    getSettingsData() {
      this.settingsLoading = true
      Api.PaymentReminderConfig.show()
        .then((res) => {
          const attrs = res?.data?.data?.attributes || {}
          const values = attrs.strategies || (attrs.strategy ? [attrs.strategy] : [])
          this.selectedStrategies = this.normalizeStrategies(values)
        })
        .catch((err) => {
          console.error(err)
          this.addNotification({ type: 'error', msg: 'Não foi possível carregar as configurações de envio.' })
        })
        .finally(() => {
          this.settingsLoading = false
        })
    },
    saveSettings() {
      if (!this.canEditWppSettings) {
        this.addNotification({ type: 'error', msg: 'Apenas diretores podem editar as configurações.' })
        return
      }
      if (!this.selectedStrategies.length) return
      this.settingsSaving = true
      Api.PaymentReminderConfig.update({ strategies: this.normalizeStrategies(this.selectedStrategies) })
        .then((res) => {
          const attrs = res?.data?.data?.attributes || {}
          const values = attrs.strategies || (attrs.strategy ? [attrs.strategy] : this.selectedStrategies)
          this.selectedStrategies = this.normalizeStrategies(values)
          this.addNotification({ type: 'success', msg: 'Configuração atualizada com sucesso' })
        })
        .catch((err) => {
          console.error(err)
          this.addNotification({ type: 'error', msg: 'Não foi possível atualizar as configurações.' })
        })
        .finally(() => {
          this.settingsSaving = false
        })
    },
    parseQrcodeDataUrl(body) {
      if (body == null || typeof body !== 'object') return null
      let raw =
        body.base64 ||
        body.Base64 ||
        body.data?.base64 ||
        body.data?.Base64 ||
        body.qrcode?.base64 ||
        body.qrcode?.Base64
      if (typeof raw !== 'string' || !raw.trim()) return null
      raw = raw.trim()
      if (raw.startsWith('data:image/')) return raw
      if (raw.startsWith('data:')) return raw
      return `data:image/png;base64,${raw}`
    },
    getWhatsappStatus() {
      this.instanceLoading = true
      this.instanceQrcode = null
      Api.WhatsappInstanceApi.instanceStatus()
        .then((res) => {
          this.whatsappInstanceStatus = res?.data || null
          const status = this.whatsappInstanceStatus?.status
          const st = status?.instance?.state || status?.state
          if (st && ['connecting', 'close'].includes(String(st).toLowerCase())) {
            return this.getWhatsappQrcode(this.whatsappInstanceStatus?.instance_name)
          }
          this.instanceQrcode = null
        })
        .catch((err) => {
          console.error(err)
          const msg = err?.response?.data?.error || 'Não foi possível atualizar o status.'
          this.whatsappInstanceStatus = { instance_name: 'CimentaoPrincipal', status: { error: msg } }
          this.instanceQrcode = null
        })
        .finally(() => {
          this.instanceLoading = false
        })
    },
    getWhatsappQrcode(instanceName) {
      const name = instanceName || 'CimentaoPrincipal'
      this.instanceQrcodeLoading = true
      return Api.WhatsappInstanceApi.getQrcode(name)
        .then((res) => {
          let data = res?.data
          if (typeof data === 'string') {
            try {
              data = JSON.parse(data)
            } catch (_) {
              data = null
            }
          }
          this.instanceQrcode = this.parseQrcodeDataUrl(data)
        })
        .catch(() => {
          this.instanceQrcode = null
        })
        .finally(() => {
          this.instanceQrcodeLoading = false
        })
    },
  },
  watch: {
    'pagination.page'(page, prev) {
      if (prev === undefined) return
      if (page === prev) return
      if (this.loading) return
      this.loadMessages()
    },
    'pagination.itemsPerPage'(perPage, prev) {
      if (prev === undefined) return
      if (perPage === prev) return
      if (this.loading) return
      this.loadMessages(true)
    },
  },

}
</script>

<style lang="scss">
/* Tooltip é renderizado no overlay; precisa de classe global (sem scoped). */
.whatsapp-messages-tooltip.v-tooltip__content {
  background-color: #121212 !important;
  color: #fff !important;
  opacity: 1 !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
}

.whatsapp-number-cell {
  font-variant-numeric: tabular-nums;
  font-size: 0.8125rem;
  white-space: nowrap;
}

.whatsapp-messages-filters {
  width: 100%;
  flex: 1 1 100%;
  min-width: 0;
}

.whatsapp-messages-filters .filter-data-card {
  width: 100%;
}
</style>
